import { useState } from "react";
import { Button, Dialog, EmptyState, Icon, Notice, Skeleton, TextField, Total, type IconName } from "../ui";
import { ItemRow } from "../ui/ItemRow";
import { ICONS } from "../ui/Icon";
import { ApiFehler } from "../api/client";
import { parseEuro } from "@shared/geld";
import type { WarenkorbAnsicht } from "./api";
import { useKachelHinzufuegen, useKacheln, type Kachel } from "./sonderfaelle-api";

/** Ladeneigene Ware: Kacheln aus den Einstellungen; Erlös geht an den Laden, nicht an einen Mieter. */
export function Ladenware({ warenkorb, onZurueck }: { warenkorb: WarenkorbAnsicht; onZurueck: () => void }) {
  const kacheln = useKacheln();
  const hinzu = useKachelHinzufuegen();
  const [frei, setFrei] = useState<Kachel | null>(null);
  const [betrag, setBetrag] = useState("");
  const [fehler, setFehler] = useState<string | null>(null);
  const anzahl = (k: Kachel) => warenkorb.positionen.filter((p) => p.art === "ladenware" && p.bezeichnung === k.name).length;

  async function hinzufuegen(k: Kachel, preisCent?: number) {
    setFehler(null);
    try {
      await hinzu.mutateAsync({ id: warenkorb.id, kachelId: k.id, preisCent });
      setFrei(null);
      setBetrag("");
    } catch (err) {
      setFehler(err instanceof ApiFehler ? err.message : "Das hat nicht geklappt.");
    }
  }

  return (
    <div className="sc-kasse">
      <section className="sc-kasse__cart kasse-flow">
        <div className="sc-row">
          <Button variant="quiet" icon="chevron-left" onClick={onZurueck} aria-label="Zurück zum Warenkorb" />
          <div className="sc-col" style={{ gap: 0 }}>
            <h1 className="title">Ladenware hinzufügen</h1>
            <span className="sc-muted sc-small">Artikel des Ladens, ohne Mieter · tippen zum Hinzufügen</span>
          </div>
        </div>
        {fehler ? <Notice tone="error">{fehler}</Notice> : null}
        {kacheln.isPending ? (
          <Skeleton lines={2} />
        ) : !kacheln.data?.kacheln.length ? (
          <EmptyState compact illustration="empty-shelf" title="Noch keine Kacheln">
            Kacheln legt der Inhaber in der Verwaltung unter Einstellungen › Kasse & SumUp an (z. B. Tragetasche, Kaffee, freier Betrag).
          </EmptyState>
        ) : (
          <div className="sc-tiles">
            {kacheln.data.kacheln.map((k) => {
              const n = anzahl(k);
              const icon = (k.icon in ICONS ? k.icon : "store") as IconName;
              return (
                <button key={k.id} type="button" className="sc-tile" aria-pressed={n > 0} disabled={hinzu.isPending} onClick={() => (k.preisCent === null ? setFrei(k) : void hinzufuegen(k))}>
                  <span className="sc-row" style={{ justifyContent: "space-between", width: "100%" }}>
                    <Icon name={icon} size={24} />
                    {n > 0 ? <span className="rg-badge rg-badge--petrol">{n} im Warenkorb</span> : null}
                  </span>
                  <span className="sc-col" style={{ gap: 0 }}>
                    <span className="sc-strong">{k.name}</span>
                    <span className="sc-muted">{k.preisCent === null ? "Betrag frei wählen" : `${(k.preisCent / 100).toLocaleString("de-DE", { minimumFractionDigits: 2 })} €`}</span>
                  </span>
                </button>
              );
            })}
          </div>
        )}
        <p className="sc-muted sc-small">Ladenware wird dem Laden gutgeschrieben, nicht einem Mieter.</p>
      </section>
      <aside className="sc-kasse__side">
        <Total amountCent={warenkorb.summeCent} caption={`${warenkorb.positionen.length} Artikel · davon ${warenkorb.positionen.filter((p) => p.art === "ladenware").length} Ladenware`} size="lg" />
        <div style={{ overflowY: "auto" }}>
          {warenkorb.positionen.map((p) => (
            <ItemRow key={p.id} title={p.bezeichnung} photo={false} meta={p.art === "ladenware" ? "Laden" : [p.mieterName, p.regalNummer && `Regal ${p.regalNummer}`].filter(Boolean).join(" · ")} price={p.endpreisCent} selected={p.art === "ladenware"} />
          ))}
        </div>
        <span className="sc-grow" />
        <Button variant="primary" size="lg" block onClick={onZurueck}>
          Zurück zum Scannen
        </Button>
      </aside>
      <Dialog
        open={!!frei}
        title={frei?.name ?? ""}
        onClose={() => setFrei(null)}
        size="sm"
        actions={
          <>
            <Button variant="quiet" onClick={() => setFrei(null)}>
              Abbrechen
            </Button>
            <Button variant="primary" icon="plus" disabled={!parseEuro(betrag) || parseEuro(betrag)! <= 0} loading={hinzu.isPending} onClick={() => frei && void hinzufuegen(frei, parseEuro(betrag)!)}>
              Hinzufügen
            </Button>
          </>
        }
      >
        <TextField label="Betrag" value={betrag} onChange={(e) => setBetrag(e.target.value)} inputMode="decimal" suffix="€" placeholder="0,00" autoFocus onKeyDown={(e) => e.key === "Enter" && frei && parseEuro(betrag) && void hinzufuegen(frei, parseEuro(betrag)!)} />
      </Dialog>
    </div>
  );
}
